// lib/api/client-routes.ts
// URLs for the few api02 reads that name a RESOURCE the caller is not scoped to by
// default: another agency the platform-administrator surfaces browse, and an office or
// staff member addressed by id.
//
// A client never states who it is — the session does that, and every handler here takes
// its tenant from the caller. What these builders express is a selection: which agency an
// administrator is currently looking at. What the server does with that selection is NOT
// uniform, so each builder states its own rule rather than the file claiming one:
//
//   agencyByIdUrl          GET /agency is a `publicRoute` (widget boot config). Anonymous
//                          callers get the redacted document for any id; an AUTHENTICATED
//                          caller naming another tenant gets 404.
//   subAgencyByIdUrl       an office by id inside the caller's own agency; a foreign id
//                          answers 404.
//   subAgenciesByParentUrl the caller's own offices; another agency named here is ignored.
//   staffByAgencyUrl       `withAdmin`: 401/403 for everyone who is not `users.admin`.
//   staffByUserUrl         the same.
//   offboardStaffUrl       the same.
//   leadsByAgencyUrl       the caller's own leads; another agency named here is ignored.
//   impressionsLatestUrl   the same.
//
// The administration console never uses the tenant-scoped builders to reach another
// agency: it has its own `admin*` builders at the end of this file, whose routes are
// `withAdmin` and cross the tenant on purpose.
//
// They live in `lib/` rather than beside their callers on purpose: `REQ-CLIENT-02` scans
// client code for identity parameters line by line and cannot tell an administrator's
// selection from a caller naming itself, so the exception is stated once, here, where it
// can be reviewed — instead of being repeated across a dozen admin screens.

/** One agency's full document. Anonymous for the widget's boot config; scoped otherwise. */
export function agencyByIdUrl(agencyId: string): string {
  return `/api/agency?agencyId=${encodeURIComponent(agencyId)}`;
}

/** One office by id, inside the caller's own agency. */
export function subAgencyByIdUrl(subAgencyId: string): string {
  return `/api/sub-agencies?subAgencyId=${encodeURIComponent(subAgencyId)}`;
}

/** The caller's own offices; another agency named here is ignored. */
export function subAgenciesByParentUrl(agencyId: string): string {
  return `/api/sub-agencies?parentAgencyId=${encodeURIComponent(agencyId)}`;
}

/** The staff of one agency (platform-administrator surface). */
export function staffByAgencyUrl(agencyId: string): string {
  return `/api/agents?agencyId=${encodeURIComponent(agencyId)}`;
}

/** One staff member by id (platform-administrator surface). */
export function staffByUserUrl(userId: string): string {
  return `/api/agents?userId=${encodeURIComponent(userId)}`;
}

/** One staff member's offboarding: GET previews, POST hands over and deletes (platform-administrator surface). */
export function offboardStaffUrl(userId: string): string {
  return `/api/admin/agents/${encodeURIComponent(userId)}/offboard`;
}

/** The caller's own leads; another agency named here is ignored. */
export function leadsByAgencyUrl(agencyId: string): string {
  return `/api/leads?agencyId=${encodeURIComponent(agencyId)}`;
}

/** The caller's own widget sessions; another agency named here is ignored. */
export function impressionsLatestUrl(agencyId: string, pageSize: number): string {
  return `/api/impressions/latest?agencyId=${encodeURIComponent(agencyId)}&pageSize=${pageSize}`;
}

// ---------------------------------------------------------------------------
// api03 — the two communications reads the administration console browses by
// agency. Same rule as the api02 builders above: another agency named here is
// ignored; the console uses the `admin*` builders.
// ---------------------------------------------------------------------------

/** The caller's own widget chats; another agency named here is ignored. */
export function latestChatsUrl(agencyId: string, pageSize: number): string {
  return `/api/chats/latest?agencyId=${encodeURIComponent(agencyId)}&pageSize=${pageSize}`;
}

/** One page of the email-ingestion history, optionally narrowed to one agency. */
export function ingestionLogsUrl(params: {
  page: number;
  limit: number;
  agencyId?: string;
  status?: string;
  search?: string;
}): string {
  const query = new URLSearchParams({ page: String(params.page), limit: String(params.limit) });
  if (params.agencyId) query.set('agencyId', params.agencyId);
  if (params.status) query.set('status', params.status);
  if (params.search) query.set('search', params.search);
  return `/api/email-ingestion/logs?${query.toString()}`;
}

// ---------------------------------------------------------------------------
// api04 — the administration console and the report-builder wizard. Same rule as
// the builders above: another agency named here is ignored; the console uses the
// `admin*` builders.
// ---------------------------------------------------------------------------

/** The active report template for one agency and language. */
export function templateByAgencyUrl(agencyId: string, language: string, fallback = false): string {
  const query = new URLSearchParams({ agencyId, language });
  if (fallback) query.set('fallback', 'true');
  return `/api/report-templates/by-agency?${query.toString()}`;
}

/** Every active report template for one agency and language. */
export function templateListByAgencyUrl(agencyId: string, language: string, fallback = false): string {
  const query = new URLSearchParams({ agencyId, language });
  if (fallback) query.set('fallback', 'true');
  return `/api/report-templates/by-agency/list?${query.toString()}`;
}

/** The languages one agency has an active template for. */
export function templateLanguagesByAgencyUrl(agencyId: string): string {
  return `/api/report-templates/by-agency/languages?agencyId=${encodeURIComponent(agencyId)}`;
}

/** The template catalogue of one agency (report-builder wizard). */
export function templatesByAgencyUrl(agencyId: string, status?: string): string {
  const query = new URLSearchParams({ agencyId });
  if (status) query.set('status', status);
  return `/api/report-templates?${query.toString()}`;
}

/** Platform usage for one agency (administration console). */
export function adminPlatformUsageUrl(agencyId: string): string {
  return `/api/admin/platform-usage?agencyId=${encodeURIComponent(agencyId)}`;
}

/** One administration analytics report, optionally narrowed to one agency. */
export function adminAnalyticsUrl(report: string, params: { from?: string; to?: string; agencyId?: string }): string {
  const query = new URLSearchParams();
  if (params.from) query.set('from', params.from);
  if (params.to) query.set('to', params.to);
  if (params.agencyId) query.set('agencyId', params.agencyId);
  const search = query.toString();
  return `/api/admin/analytics/${report}${search ? `?${search}` : ''}`;
}

/** Cross-agency lead matching for one agency (platform-administrator surface). */
export function adminLeadMatchingUrl(agencyId: string): string {
  return `/api/admin/lead-matching?agencyId=${encodeURIComponent(agencyId)}`;
}

// ---------------------------------------------------------------------------
// The administration console's own routes. Every one is `withAdmin` and names the
// agency it acts on in the path (or as an optional platform-list filter).
// ---------------------------------------------------------------------------

/** One agency's document (GET) or its edit (PATCH). */
export function adminAgencyUrl(agencyId: string): string {
  return `/api/admin/agencies/${encodeURIComponent(agencyId)}`;
}

export function adminAgencyConfigUrl(agencyId: string): string {
  return `${adminAgencyUrl(agencyId)}/config`;
}

export function adminAgencyStatusUrl(agencyId: string): string {
  return `${adminAgencyUrl(agencyId)}/status`;
}

export function adminAgencySettingsUrl(agencyId: string): string {
  return `${adminAgencyUrl(agencyId)}/settings`;
}

export function adminAgencyAgentsUrl(agencyId: string): string {
  return `${adminAgencyUrl(agencyId)}/agents`;
}

/** Runs the lead agent on one lead of the agency with writes disabled (POST `{ leadId }`). */
export function adminAgencyAgentDryRunUrl(agencyId: string): string {
  return `${adminAgencyUrl(agencyId)}/agent-dry-run`;
}


/** The staff who may read a shared mailbox for the agent (GET), or a read test of one (POST `{ address, viaUserId }`). */
export function adminAgencySharedMailboxesUrl(agencyId: string): string {
  return `${adminAgencyUrl(agencyId)}/shared-mailboxes`;
}

/** An agency's offices (GET) or a new one (POST). */
export function adminOfficesUrl(agencyId: string): string {
  return `${adminAgencyUrl(agencyId)}/offices`;
}

/** One office of an agency (PATCH, DELETE). */
export function adminOfficeUrl(agencyId: string, subAgencyId: string): string {
  return `${adminOfficesUrl(agencyId)}/${encodeURIComponent(subAgencyId)}`;
}

/** Starts (POST `{ userId }`) or stops (DELETE) the administrator acting as a staff user. */
export function adminImpersonationUrl(): string {
  return '/api/admin/impersonation';
}

/** An agency's leads, narrowed like `/api/leads` (`startDate`, `endDate`, `source`, …). */
export function adminAgencyLeadsUrl(agencyId: string, params: Record<string, string> = {}): string {
  const query = new URLSearchParams(params).toString();
  return `${adminAgencyUrl(agencyId)}/leads${query ? `?${query}` : ''}`;
}

export function adminLegacyPriorityUrl(leadId: string): string {
  return `/api/admin/leads/${encodeURIComponent(leadId)}/legacy-priority`;
}

/** Empty filters are left out; `agencyId`, when given, always comes last. */
function platformListUrl(path: string, params: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (key !== 'agencyId' && value !== undefined && value !== '') query.set(key, String(value));
  }
  if (params.agencyId) query.set('agencyId', String(params.agencyId));
  return `${path}?${query.toString()}`;
}

/** Widget sessions of one agency, or of the platform when `agencyId` is omitted. */
export function adminImpressionsUrl(params: { agencyId?: string; page?: number; pageSize: number }): string {
  return platformListUrl('/api/admin/impressions', params);
}

/** Widget chats of one agency, or of the platform when `agencyId` is omitted. */
export function adminChatsUrl(params: { agencyId?: string; page?: number; pageSize: number }): string {
  return platformListUrl('/api/admin/chats', params);
}

/** Email-ingestion logs of one agency, or of the platform when `agencyId` is omitted. */
export function adminIngestionLogsUrl(params: {
  page: number;
  limit: number;
  agencyId?: string;
  status?: string;
  search?: string;
}): string {
  return platformListUrl('/api/admin/email-ingestion/logs', params);
}

/** One page of an agency's judged agent runs, narrowed by the monitor filter. */
export function adminAgentRunsUrl(agencyId: string, params: { filter: string; page: number }): string {
  return `/api/admin/agencies/${encodeURIComponent(agencyId)}/agent-runs?filter=${encodeURIComponent(params.filter)}&page=${params.page}`;
}

/** One judged agent run of an agency, with its trace and verdict. */
export function adminAgentRunUrl(agencyId: string, runId: string): string {
  return `/api/admin/agencies/${encodeURIComponent(agencyId)}/agent-runs/${encodeURIComponent(runId)}`;
}

/** The administrator's review of one judged agent run. */
export function adminAgentRunReviewUrl(agencyId: string, runId: string): string {
  return `${adminAgentRunUrl(agencyId, runId)}/review`;
}

/** Judges one agent run of an agency now. */
export function adminAgentRunJudgeUrl(agencyId: string, runId: string): string {
  return `${adminAgentRunUrl(agencyId, runId)}/judge`;
}

/** One agent lesson of an agency (switch it on or off). */
export function adminAgentLessonUrl(agencyId: string, lessonId: string): string {
  return `/api/admin/agencies/${encodeURIComponent(agencyId)}/agent-lessons/${encodeURIComponent(lessonId)}`;
}

export const ADMIN_USERS_BATCH_URL = '/api/admin/users/batch';
export const ADMIN_CONVERSATIONS_BATCH_URL = '/api/admin/conversations/batch';
export const ADMIN_LEADS_BATCH_URL = '/api/admin/leads/batch';

/** One report template of any agency (report builder). */
export function adminReportTemplateUrl(templateId: string): string {
  return `/api/admin/report-templates/${encodeURIComponent(templateId)}`;
}

/** The test fixture of one report template of any agency (report builder). */
export function adminTestFixtureUrl(templateId: string): string {
  return `${adminReportTemplateUrl(templateId)}/test-fixture`;
}

/** Review links of one template of any agency (GET with `templateId`), or a new one (POST). */
export function adminTemplateReviewsUrl(templateId?: string): string {
  return templateId
    ? `/api/admin/template-reviews?templateId=${encodeURIComponent(templateId)}`
    : '/api/admin/template-reviews';
}

/** One review link (PATCH to resolve it). */
export function adminTemplateReviewUrl(token: string): string {
  return `/api/admin/template-reviews/${encodeURIComponent(token)}`;
}

/** A user's saved HTML email signature, inside the caller's agency. */
export function userSignatureUrl(userId: string): string {
  return `/api/users/${encodeURIComponent(userId)}/signature`;
}

/** A user's saved signatures; `discover` reads candidates from the caller's own sent mail. */
export function userSignaturesUrl(userId: string, discover = false): string {
  return `/api/users/${encodeURIComponent(userId)}/signatures${discover ? '?discover=1' : ''}`;
}

/** The Agent folder: the agent's items on the caller's own leads. */
export function agentItemsUrl(): string {
  return '/api/agent/items';
}

/** One agent item (PATCH edits what an auto action produced). */
export function agentItemUrl(itemId: string): string {
  return `/api/agent/items/${encodeURIComponent(itemId)}`;
}

/** The caller's own leads matching `q`, for the chat box's @-mentions. */
export function agentLeadMentionsUrl(q: string): string {
  return `/api/agent/leads/mentions?q=${encodeURIComponent(q)}`;
}

export function agentChatUrl(): string {
  return '/api/agent/chat';
}

/** The salesperson's context on one lead; the agent re-runs on it. */
export function agentLeadContextUrl(leadId: string): string {
  return `/api/agent/leads/${encodeURIComponent(leadId)}/context`;
}

/** The lead's "Waar staan we" lines (PATCH edits them or marks them seen). */
export function agentLeadSummaryUrl(leadId: string): string {
  return `/api/agent/leads/${encodeURIComponent(leadId)}/summary`;
}

/** Where the agent's re-run on one lead stands since `since` (GET), or a retry of it (POST). */
export function agentLeadRunUrl(leadId: string, since?: string): string {
  const base = `/api/agent/leads/${encodeURIComponent(leadId)}/run`;
  return since ? `${base}?since=${encodeURIComponent(since)}` : base;
}

/** The agent's queued, running and just-finished runs on the caller's own leads. */
export function agentActiveRunsUrl(): string {
  return '/api/agent/runs/active';
}

/** The caller's own notes of today, one per lead. */
export function agentNotesTodayUrl(): string {
  return '/api/agent/notes-today';
}

/** The full texts one lead's notes were summarised from. */
/** POST — a recorded call's audio, transcribed again with speaker separation. */
export function agentLeadCallAudioUrl(leadId: string): string {
  return `/api/agent/leads/${encodeURIComponent(leadId)}/call-audio`;
}

export function agentLeadTranscriptsUrl(leadId: string): string {
  return `/api/agent/leads/${encodeURIComponent(leadId)}/transcripts`;
}

/** The salesperson's messages to the agent on one lead. */
export function agentLeadMessagesUrl(leadId: string): string {
  return `/api/agent/leads/${encodeURIComponent(leadId)}/messages`;
}

export function agentItemDecisionUrl(itemId: string): string {
  return `${agentItemUrl(itemId)}/decision`;
}

export function agentItemSendUrl(itemId: string): string {
  return `${agentItemUrl(itemId)}/send`;
}

export function agentItemFeedbackUrl(itemId: string): string {
  return `${agentItemUrl(itemId)}/feedback`;
}

export function agentItemUndoUrl(itemId: string): string {
  return `${agentItemUrl(itemId)}/undo`;
}

export function agentItemRedoUrl(itemId: string): string {
  return `${agentItemUrl(itemId)}/redo`;
}

